// Librairies
import i18n from '../locales/i18n';
// Utils
import FormulasUtil from './FormulasUtil';
import GreenSpacesUtil from './GreenSpacesUtil';
import PatternsUtil from './PatternsUtil';
import TreesUtil from './TreesUtil';

const MISSING_DATA_KEY = '-1';
const EMPTY_LOCATION_KEY = 'empty';
const ALL_ELEMENTS_KEY = '__all__';

const MISSING_DATA_COLOR = 'rgb(255, 255, 255)';
const EMPTY_LOCATION_COLOR = 'rgb(153,153,255)';

const CUT_DOWN_KEEPING_STYLE = () => [i18n.t("Aucun"), i18n.t("Abattages"), i18n.t("Mortalité"), i18n.t("Cotes sanitaires")];
const CUT_DOWN_KEEPING_PROPERTIES = ['mortality', 'deathCauseId', 'healthReviewId'];

const HEALTH_REVIEW_COLORS = [
    'rgb(0,0,0)', 'rgb(255,0,0)', 'rgb(200,0,0)', 'rgb(255,192,0)', 'rgb(255,166,0)', 'rgb(255,255,0)',
    'rgb(230,230,0)', 'rgb(146,208,80)', 'rgb(108,168,62)', 'rgb(0,176,80)', 'rgb(0,130,60)'
];

const ONTOGENIC_STAGE_COLORS = [
    'rgb(0,0,0)', 'rgb(99,190,123)', 'rgb(116,195,126)', 'rgb(133,200,130)', 'rgb(151,206,134)', 'rgb(168,211,137)',
    'rgb(185,217,141)', 'rgb(203,222,145)', 'rgb(220,228,148)', 'rgb(237,233,152)', 'rgb(255,239,156)'
];

const ACCURATE_RISK_BUCKETS = [
    { max: 5, color: 'rgb(0,130,60)' }, { max: 7, color: 'rgb(146,208,80)' }, { max: 9, color: 'rgb(255,255,0)' },
    { max: 11, color: 'rgb(255,166,0)' }, { max: Infinity, color: 'rgb(255,0,0)' }
];

const STABILITY_BUCKETS = [
    { max: 30, color: 'rgb(0,130,60)' }, { max: 50, color: 'rgb(146,208,80)' },
    { max: 70, color: 'rgb(255,192,0)', isInclusive: true }, { max: Infinity, color: 'rgb(255,0,0)' }
];

const CUT_DOWN_COLORS = ['rgb(144,238,144)', 'rgb(0,176,80)', 'rgb(255,255,0)', 'rgb(255,165,0)', 'rgb(255,0,0)', 'rgb(0,0,0)'];

const ACTIONS_URGENCY_COLORS = [
    '#fcfcfc', // pas d'action
    '#7d7d7d', // annulée
    '#68bd46', // validée
    '#1a5276', // à venir + 1 mois
    '#17a2b8', // à venir - 1 mois
    '#c83030'  // en retard
];

const MORTALITY_COLORS = { alive: '#68bd46', dead: '#141414', stump: '#a2653d', chronoxyle: '#ce4894', deadEmpty: '#fcfcfc', empty: '#fcfcfc' };

const AGE_COLORS = ['grey', 'green', 'lime', 'yellow', 'orange', 'red', 'brown'];

const HEIGHT_BUCKETS = [
    { key: '0-5', label: '< 5', color: 'var(--secondary-100)', isIncluded: (height) => height < 5 },
    { key: '5-10', label: '5-10', color: 'var(--primary-100)', isIncluded: (height) => height <= 10 },
    { key: '10-15', label: '10-15', color: 'var(--yellow-100)', isIncluded: (height) => height <= 15 },
    { key: '15-20', label: '15-20', color: 'var(--orange-100)', isIncluded: (height) => height <= 20 },
    { key: '20-25', label: '20-25', color: 'var(--red-100)', isIncluded: (height) => height <= 25 },
    { key: '25+', label: '> 25', color: 'var(--purple-100)', isIncluded: () => true }
];

const MANAGEMENT_CLASS_COLORS = { 1: 'var(--pink-100)', 2: 'var(--blue-100)', 3: 'var(--yellow-100)', 4: 'var(--green-100)', 5: 'var(--purple-100)' };

const STATION_COLORS = { 1: 'var(--red-110)', 2: 'var(--red-60)', 3: 'var(--red-10)' };

const DONE_STATUSES = ['done', 'cancelled'];

let classificationCache = null;

const REFERENTIAL_LISTS = ['healthReviews', 'ontogenicStages', 'plantationCoefficients', 'situationCoefficients', 'patrimonialCoefficients', 'managementClasses'];
const INDEXED_LISTS = ['essences', 'projectActions', 'dominantCompositions', 'runoffCoefficients', 'spaceTypes', 'tippingRisks', 'organCalibers', 'targets', ...REFERENTIAL_LISTS];

let contextIndexesCache = null;

const indexById = (list) => new Map((list || []).map(item => [item.id, item]));

const buildContextIndexes = (context) => {
    const actionElementsByElementId = new Map();
    (context.projectActions || []).forEach(projectAction => {
        (projectAction.projectActionElements || []).forEach(projectActionElement => {
            const entry = { projectAction, projectActionElement };
            const entries = actionElementsByElementId.get(projectActionElement.elementId);
            if (entries) entries.push(entry);
            else actionElementsByElementId.set(projectActionElement.elementId, [entry]);
        });
    });

    return {
        actionElementsByElementId,
        essenceById: indexById(context.essences),
        dominantCompositionById: indexById(context.dominantCompositions),
        runoffCoefficientById: indexById(context.runoffCoefficients),
        spaceTypeById: indexById(context.spaceTypes),
        tippingRiskById: indexById(context.tippingRisks),
        organCaliberById: indexById(context.organCalibers),
        targetById: indexById(context.targets),
        referentialsByName: REFERENTIAL_LISTS.reduce((prevValue, listName) => ({ ...prevValue, [listName]: indexById(context[listName]) }), {})
    };
};

const getContextIndexes = (context) => {
    if (contextIndexesCache && INDEXED_LISTS.every(listName => context[listName] === contextIndexesCache.key[listName]))
        return contextIndexesCache.indexes;

    const indexes = buildContextIndexes(context);
    contextIndexesCache = { key: INDEXED_LISTS.reduce((prevValue, listName) => ({ ...prevValue, [listName]: context[listName] }), {}), indexes };
    return indexes;
};

export default class ClassificationUtil {
    static getMissingDataKey = () => MISSING_DATA_KEY;

    static getEmptyLocationKey = () => EMPTY_LOCATION_KEY;

    static getAllElementsKey = () => ALL_ELEMENTS_KEY;

    static getMissingDataColor = () => MISSING_DATA_COLOR;

    static getEmptyLocationColor = () => EMPTY_LOCATION_COLOR;

    static getHealthReviewColor = (healthReview) => !healthReview && healthReview !== 0
        ? EMPTY_LOCATION_COLOR
        : HEALTH_REVIEW_COLORS[healthReview] || HEALTH_REVIEW_COLORS[HEALTH_REVIEW_COLORS.length - 1];

    static getVigorColor = (vigor) => !vigor ? EMPTY_LOCATION_COLOR :
        vigor === i18n.t('Mort') ? 'rgb(0,0,0)' :
            vigor === i18n.t('Faible') ? 'rgb(255,0,0)' :
                vigor === i18n.t('Moyenne') ? 'rgb(255,192,0)' :
                    'rgb(0,176,80)';

    static getRiskColor = (risk) => !risk ? EMPTY_LOCATION_COLOR :
        risk === i18n.t('Faible') ? 'rgb(0,176,80)' :
            risk === i18n.t('Moyen') ? 'rgb(255,192,0)' :
                risk === i18n.t('Élevé') ? 'rgb(255,0,0)' :
                    'rgb(0,176,80)';

    static getAccurateRiskColor = (accurateRisk) => !accurateRisk
        ? EMPTY_LOCATION_COLOR
        : ACCURATE_RISK_BUCKETS.find(bucket => accurateRisk < bucket.max).color;

    static getOntogenicStageColor = (ontogenicStage) => !ontogenicStage && ontogenicStage !== 0
        ? EMPTY_LOCATION_COLOR
        : ONTOGENIC_STAGE_COLORS[ontogenicStage] || ONTOGENIC_STAGE_COLORS[ONTOGENIC_STAGE_COLORS.length - 1];

    static getArchiStageColor = (archiStage) => !archiStage ? EMPTY_LOCATION_COLOR :
        archiStage === i18n.t("Jeune") ? '#b8d4ac' :
            archiStage === i18n.t("Adulte") ? '#70ac4c' :
                archiStage === i18n.t("Mature") ? '#c09404' :
                    '#803c04';

    static getArchiStateColor = (archiState) => !archiState ? EMPTY_LOCATION_COLOR :
        archiState === i18n.t("Sain") ? '#92d04f' :
            archiState === i18n.t("Stressé") ? '#fdff05' :
                archiState === i18n.t("Résilient") ? '#92d04f' :
                    archiState === i18n.t("Descente de cime") ? '#92d04f' :
                        archiState === i18n.t("Repli") ? '#fdff05' :
                            archiState === i18n.t("Dépérissement irréversible") ? '#ffc000' :
                                '#080404';

    static getStabilityColor = (stability) => STABILITY_BUCKETS
        .find(bucket => bucket.isInclusive ? stability <= bucket.max : stability < bucket.max).color;

    static getConditionColor = (condition) => !condition ? EMPTY_LOCATION_COLOR :
        condition === i18n.t('Très bon') ? 'rgb(0,176,44)' :
            condition === i18n.t('Correct') ? 'rgb(0,176,80)' :
                condition === i18n.t('Dégradé') ? 'rgb(255,192,0)' :
                    'rgb(255,0,0)';

    static getHeightColor = (height) => !height
        ? MISSING_DATA_COLOR
        : HEIGHT_BUCKETS.find(bucket => bucket.isIncluded(height)).color;

    static getAgeColor = (age) => AGE_COLORS[TreesUtil.getAgeIndex(age)];

    static getCutDownColor = (toCutDown) => CUT_DOWN_COLORS[toCutDown] || CUT_DOWN_COLORS[0];

    static getActionsUrgencyColor = (actionsUrgency) => ACTIONS_URGENCY_COLORS[actionsUrgency] || ACTIONS_URGENCY_COLORS[0];

    static getMortalityColor = (properties) => MORTALITY_COLORS[ClassificationUtil.getMortalityKey(properties)];

    static getManagementClassColor = (managementClass) => MANAGEMENT_CLASS_COLORS[managementClass] || MISSING_DATA_COLOR;

    static getStationColor = (level, fieldList) => fieldList?.stationLevels?.find(({ level: stationLevel }) => stationLevel === level)?.color
        || STATION_COLORS[level || 1] || STATION_COLORS[1];

    static getDominantCompositionColor = (dominantComposition, raw = true) => {
        switch (dominantComposition) {
            case i18n.t("Fleurissement suspension"): case i18n.t("Pré de fauche"): case i18n.t("Prairie fleurie"): case i18n.t("Massif mixte vivaces/graminées/arbustes"): case i18n.t("Revêtement perméable"): case i18n.t("Revêtement semi-perméable"):
            case i18n.t("Haie libre"): case i18n.t("Haie stricte"): case i18n.t("Zone humide"): case i18n.t("Friche"): case i18n.t("Verger"): case i18n.t("Potager"): case i18n.t("Végétation herbacée spontanée"):
            case i18n.t("Broyat"): case i18n.t("Végétation verticale ou sur toit"): case i18n.t("Zone agricole"):
                const pattern = PatternsUtil.getPatternByLabel(dominantComposition);
                return raw ? pattern?.options.svg : { fillPattern: pattern };
            case i18n.t("Pelouse"): return raw ? '#ABF3BD' : { fillColor: '#ABF3BD' };
            case i18n.t("Massif arbustif"): return raw ? '#31982E' : { fillColor: '#31982E' };
            case i18n.t("Massif vivaces"): return raw ? '#FF8000' : { fillColor: '#FF8000' };
            case i18n.t("Massif arboré"): return raw ? '#1F611D' : { fillColor: '#1F611D' };
            case i18n.t("Revêtement imperméable"): return raw ? '#888888' : { fillColor: '#888888' };
            case i18n.t("Eau libre"): return raw ? '#B3D9FF' : { fillColor: '#B3D9FF' };
            case i18n.t("Terre nue"): return raw ? '#926821' : { fillColor: '#926821' };
            case i18n.t("Terrain synthétique"): return raw ? '#6e286d' : { fillColor: '#6e286d' };
            case i18n.t("Fleurissement massif"): return raw ? '#f19ef9' : { fillColor: '#f19ef9' };
            case i18n.t("Fleurissement bac"): return raw ? '#ffff54' : { fillColor: '#ffff54' };
            case i18n.t("Invasive"): return raw ? '#ea3323' : { fillColor: '#ea3323' };
            default: return raw ? MISSING_DATA_COLOR : { fillColor: MISSING_DATA_COLOR };
        }
    };

    static getElementId = (properties = {}) => properties.treeId ?? properties.greenSpaceId ?? properties.furnitureId ?? properties.id;

    static getTreeHeight = (properties = {}) => (TreesUtil.getBiggestTrunkValueOf(properties, 'height') || 0) / 100;

    static getMortalityKey = (properties = {}) => properties.isDead
        ? (properties.isStump ? 'stump' : properties.isChronoxyle ? 'chronoxyle' : properties.isEmpty ? 'deadEmpty' : 'dead')
        : properties.isEmpty ? 'empty' : 'alive';

    static isDisplayedAsEmptyLocation = (activeChild, properties = {}, thematicMap = null) => {
        if (properties.category !== 'Arbre' || properties.toCutDown !== 5) return false;
        return thematicMap
            ? !CUT_DOWN_KEEPING_PROPERTIES.includes(thematicMap.styles?.find(style => style.type === 'color')?.property)
            : !CUT_DOWN_KEEPING_STYLE().includes(activeChild);
    };

    static getUnmatchedColor = (category) => category === 'Espace vert' ? EMPTY_LOCATION_COLOR : MISSING_DATA_COLOR;

    static countByKey = (layers = [], classification) => {
        const counts = new Map();
        if (!classification) return counts;

        layers.forEach(layer => {
            const properties = layer.feature?.properties;
            if (!properties) return;
            const key = classification.getKey(properties);
            counts.set(key, (counts.get(key) || 0) + 1);
        });

        return counts;
    };

    static getThematicMapPropertyValue = (style, context = {}, properties = {}) => {
        const property = style.property;
        const elementId = ClassificationUtil.getElementId(properties);
        const indexes = getContextIndexes(context);
        const actionElements = indexes.actionElementsByElementId.get(elementId) || [];
        let propertyValue;

        if (property?.includes('action-')) {
            const actionId = +property.split('-')[1];
            return properties.toCutDown !== 5 && actionElements.some(({ projectAction, projectActionElement }) =>
                projectAction.actionId === actionId
                && (projectActionElement.projectActionElementRecurrences || []).some(recurrence => !DONE_STATUSES.includes(recurrence.status)));
        }

        switch (property) {
            case 'actionId':
                const handledActions = new Set();
                const recurrences = [];
                actionElements.forEach(({ projectAction, projectActionElement }) => {
                    if (handledActions.has(projectAction)) return;
                    if (properties.category && !projectAction.action?.categories?.includes(properties.category)) return;
                    handledActions.add(projectAction);
                    (projectActionElement.projectActionElementRecurrences || [])
                        .filter(recurrence => !DONE_STATUSES.includes(recurrence.status) && (style.useLateActions || new Date(recurrence.date) > new Date()))
                        .forEach(recurrence => recurrences.push({ ...recurrence, actionId: projectAction.action.id }));
                });

                if (recurrences.length) {
                    recurrences.sort((a, b) => new Date(a.date) - new Date(b.date));
                    propertyValue = recurrences[0].actionId;
                } else propertyValue = 0;
                break;
            case 'order': case 'gender': case 'species': case 'cultivar': case 'vernacularName':
                propertyValue = indexes.essenceById.get(properties.essenceId)?.[property]; break;
            case 'isFruit':
                propertyValue = !properties.isDead && indexes.essenceById.get(properties.essenceId)?.fruitProduction > 0; break;
            case 'fruitProduction':
                propertyValue = properties.isDead ? 0 : indexes.essenceById.get(properties.essenceId)?.fruitProduction; break;
            case 'toCutDown': propertyValue = [1, 2, 3, 4].includes(properties.toCutDown); break;
            case 'trunkHeight': propertyValue = properties.dimensions?.[property] / 100; break;
            case 'height': case 'circumference': case 'crownDiameter':
                // `undefined` : aucun tronc (→ 0) ; `null` : tronc sans mesure
                const mesure = TreesUtil.getBiggestTrunkValueOf(properties, property);
                propertyValue = mesure === undefined ? 0 : mesure;
                if (propertyValue && ['height', 'crownDiameter'].includes(property)) propertyValue /= 100;
                break;
            case 'runoffCoefficientId':
                const dominantComposition = indexes.dominantCompositionById.get(properties.dominantCompositionId);
                const runoffCoefficient = indexes.runoffCoefficientById.get(dominantComposition?.runoffCoefficientId);
                propertyValue = GreenSpacesUtil.getRunoffCoefficientString(runoffCoefficient);
                break;
            case 'healthReviewId': case 'ontogenicStageId': case 'plantationCoefficientId': case 'situationCoefficientId': case 'patrimonialCoefficientId': case 'managementClassId':
                const listName = property.replace('Id', property === 'managementClassId' ? 'es' : 's');
                propertyValue = style.useNumericalConditions
                    ? indexes.referentialsByName[listName]?.get(properties[property])?.value
                    : properties[property];
                break;
            case 'trunks': propertyValue = properties[property]?.length; break;
            case 'mortality': propertyValue = ClassificationUtil.getMortalityKey(properties); break;
            case 'totalCarbonStock':
                // TODO Ajouter les massifs arborés
                if (properties.category === 'Arbre')
                    propertyValue = FormulasUtil.getTreeTotalCarbonStock(properties.carbonStock, TreesUtil.getBiggestTrunkValueOf(properties, 'circumference') || 0);
                break;
            case 'coolingEconomicValue': propertyValue = FormulasUtil.getCoolingEconomicValue(properties.coolingEnergyIndicator); break;
            case 'oxygenProduction': propertyValue = FormulasUtil.getOxygenProduction(properties.carbonStock); break;
            case 'biodiversityIndex':
                propertyValue = properties.category === 'Espace vert'
                    ? (indexes.spaceTypeById.get(properties.spaceTypeId)?.ecopotentialIndex || 0) * 100
                    : properties.biodiversityIndex * 100;
                break;
            case 'canopy':
                propertyValue = Math.round((Math.pow(((TreesUtil.getBiggestTrunkValueOf(properties, 'crownDiameter') || 0) / 100) / 2, 2) * Math.PI) * 100) / 100; break;
            case 'riskScore': case 'dangerLevel':
                const accurateRisk = properties.tippingRiskId && properties.organCaliberId && properties.targetId ? (
                    indexes.tippingRiskById.get(properties.tippingRiskId)?.value
                    + indexes.organCaliberById.get(properties.organCaliberId)?.value
                    + indexes.targetById.get(properties.targetId)?.value
                ) : undefined;
                propertyValue = property === 'riskScore' ? accurateRisk : TreesUtil.getAccurateRiskLabel(accurateRisk);
                break;
            default: propertyValue = isNaN(property) ? properties[property] : properties.customFields?.[property]; break;
        }

        return propertyValue;
    };

    static getMatchingRule = (style, properties = {}, propertyValue) => (style.rules || []).find(rule => {
        if (style.propertyType === 'number' || (style.isNumeric && style.useNumericalConditions)) {
            if (properties.isEmpty) return false;
            if (rule.key.includes('-')) {
                const values = rule.key.split('-');
                return propertyValue >= Number(values[0]) && propertyValue <= Number(values[1]);
            }
            if (rule.key.includes('<')) return propertyValue < Number(rule.key.slice(1));
            if (rule.key.includes('>')) return propertyValue > Number(rule.key.slice(1));
            return propertyValue === Number(rule.key);
        }

        if (style.propertyType === 'date') {
            if (!propertyValue) return false;
            const value = new Date(propertyValue);

            if (rule.key.includes('|')) {
                const dates = rule.key.split('|').map(isoDate => new Date(isoDate));
                return value >= new Date(dates[0].getFullYear(), dates[0].getMonth(), dates[0].getDate(), 0, 0, 0)
                    && value <= new Date(dates[1].getFullYear(), dates[1].getMonth(), dates[1].getDate(), 23, 59, 59);
            }
            if (rule.key.includes('<')) {
                const date = new Date(rule.key.slice(1));
                return value < new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
            }
            if (rule.key.includes('>')) {
                const date = new Date(rule.key.slice(1));
                return value > new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
            }

            const date = new Date(rule.key);
            return date.getFullYear() === value.getFullYear() && date.getMonth() === value.getMonth() && date.getDate() === value.getDate();
        }

        return rule.key === `${propertyValue}`;
    });

    static getThematicMapStyleValue = (style, context, properties) => {
        const propertyValue = ClassificationUtil.getThematicMapPropertyValue(style, context, properties);
        return ClassificationUtil.getMatchingRule(style, properties, propertyValue)?.value;
    };

    static getThematicMapClassification = (thematicMap, style, context = {}) => {
        const category = thematicMap?.category;

        const getKey = (properties) => {
            if (ClassificationUtil.isDisplayedAsEmptyLocation(thematicMap?.id, properties, thematicMap)) return EMPTY_LOCATION_KEY;
            const propertyValue = ClassificationUtil.getThematicMapPropertyValue(style, context, properties);
            return ClassificationUtil.getMatchingRule(style, properties, propertyValue)?.key ?? MISSING_DATA_KEY;
        };

        const getColor = (key) => {
            if (key === EMPTY_LOCATION_KEY) return EMPTY_LOCATION_COLOR;
            if (key === MISSING_DATA_KEY) return ClassificationUtil.getUnmatchedColor(category);
            return (style.rules || []).find(rule => rule.key === key)?.value;
        };

        const getBuckets = () => [
            ...(style.rules || []).map(rule => ({ key: rule.key, label: rule.label, value: rule.value, color: getColor(rule.key) })),
            { key: MISSING_DATA_KEY, label: i18n.t("Donnée manquante"), color: getColor(MISSING_DATA_KEY), hideWhenEmpty: true },
            { key: EMPTY_LOCATION_KEY, label: i18n.t("Emplacement vide"), color: EMPTY_LOCATION_COLOR, hideWhenEmpty: true }
        ];

        return { propertyName: style.property, getKey, getColor, getBuckets };
    };

    static getBuiltInClassification = (activeChild, fieldList = {}) => {
        const cacheKey = [
            activeChild, i18n.language, fieldList.healthReviews, fieldList.vigors, fieldList.risks,
            fieldList.ontogenicStages, fieldList.dominantCompositions, fieldList.managementClasses, fieldList.stationLevels
        ];

        if (classificationCache && cacheKey.every((value, index) => value === classificationCache.key[index]))
            return classificationCache.classification;

        const classification = ClassificationUtil.buildBuiltInClassification(activeChild, fieldList);
        classificationCache = { key: cacheKey, classification };
        return classification;
    };

    static buildBuiltInClassification = (activeChild, fieldList = {}) => {
        switch (activeChild) {
            case i18n.t("Cotes sanitaires"): return ClassificationUtil.getHealthReviewClassification(fieldList);
            case i18n.t("Vigueurs"): return ClassificationUtil.getVigorClassification(fieldList);
            case i18n.t("Risques"): return ClassificationUtil.getRiskClassification(fieldList);
            case i18n.t("Stades ontogéniques"): return ClassificationUtil.getOntogenicStageClassification(fieldList);
            case i18n.t("Hauteurs"): return ClassificationUtil.getHeightClassification();
            case i18n.t("Âges"): return ClassificationUtil.getAgeClassification();
            case i18n.t("Abattages"): return ClassificationUtil.getCutDownClassification();
            case i18n.t("Mortalité"): return ClassificationUtil.getMortalityClassification();
            case i18n.t("Actions"): return ClassificationUtil.getActionsUrgencyClassification();
            case i18n.t("Compositions dominantes"): return ClassificationUtil.getDominantCompositionClassification(fieldList);
            case i18n.t("Classes de gestion"): return ClassificationUtil.getManagementClassClassification(fieldList);
            case i18n.t("Stations"): return ClassificationUtil.getStationClassification(fieldList);
            default: return null;
        }
    };

    static getReferentialClassification = ({ activeChild, propertyName, fields = [], getFieldValue, getLabel, getColor: getFieldColor, isReversed = true }) => {
        const valueById = new Map(fields.map(field => [field.id, getFieldValue(field)]));

        const getKey = (properties) => {
            if (ClassificationUtil.isDisplayedAsEmptyLocation(activeChild, properties)) return EMPTY_LOCATION_KEY;
            const value = valueById.get(properties[propertyName]);
            if (value !== undefined && value !== null) return String(properties[propertyName]);
            return properties.isEmpty ? EMPTY_LOCATION_KEY : MISSING_DATA_KEY;
        };

        const getColor = (key) => key === EMPTY_LOCATION_KEY ? getFieldColor()
            : key === MISSING_DATA_KEY ? MISSING_DATA_COLOR
                : getFieldColor(valueById.get(Number(key)));

        const getBuckets = () => {
            const buckets = fields.map(field => ({ key: String(field.id), label: getLabel(field), color: getFieldColor(getFieldValue(field)) }));
            if (isReversed) buckets.reverse();
            return [
                ...buckets,
                { key: MISSING_DATA_KEY, label: i18n.t("Donnée manquante"), color: MISSING_DATA_COLOR },
                { key: EMPTY_LOCATION_KEY, label: i18n.t("Emplacement vide"), color: getFieldColor() }
            ];
        };

        return { propertyName, getKey, getColor, getBuckets };
    };

    static getHealthReviewClassification = (fieldList = {}) => ClassificationUtil.getReferentialClassification({
        activeChild: i18n.t("Cotes sanitaires"), propertyName: 'healthReviewId', fields: fieldList.healthReviews || [],
        getFieldValue: (field) => field.value, getLabel: (field) => field.description, getColor: ClassificationUtil.getHealthReviewColor
    });

    static getVigorClassification = (fieldList = {}) => ClassificationUtil.getReferentialClassification({
        activeChild: i18n.t("Vigueurs"), propertyName: 'vigorId', fields: fieldList.vigors || [],
        getFieldValue: (field) => field.label, getLabel: (field) => field.label, getColor: ClassificationUtil.getVigorColor
    });

    static getRiskClassification = (fieldList = {}) => ClassificationUtil.getReferentialClassification({
        activeChild: i18n.t("Risques"), propertyName: 'riskId', fields: fieldList.risks || [],
        getFieldValue: (field) => field.label, getLabel: (field) => field.label, getColor: ClassificationUtil.getRiskColor
    });

    static getOntogenicStageClassification = (fieldList = {}) => {
        const activeChild = i18n.t("Stades ontogéniques");
        const ontogenicStages = fieldList.ontogenicStages || [];
        const valueById = new Map(ontogenicStages.map(field => [field.id, field.value]));
        const DEAD_KEY = '0';

        const getKey = (properties) => {
            if (ClassificationUtil.isDisplayedAsEmptyLocation(activeChild, properties)) return EMPTY_LOCATION_KEY;
            if (properties.isDead) return DEAD_KEY;
            const value = valueById.get(properties.ontogenicStageId);
            if (value !== undefined && value !== null) return String(properties.ontogenicStageId);
            return properties.isEmpty ? EMPTY_LOCATION_KEY : MISSING_DATA_KEY;
        };

        const getColor = (key) => key === EMPTY_LOCATION_KEY ? ClassificationUtil.getOntogenicStageColor()
            : key === MISSING_DATA_KEY ? MISSING_DATA_COLOR
                : key === DEAD_KEY ? ClassificationUtil.getOntogenicStageColor(0)
                    : ClassificationUtil.getOntogenicStageColor(valueById.get(Number(key)));

        const getBuckets = () => [
            ...ontogenicStages.map(field => ({ key: String(field.id), label: field.value, color: ClassificationUtil.getOntogenicStageColor(field.value) })).reverse(),
            { key: DEAD_KEY, label: i18n.t("Mort"), color: ClassificationUtil.getOntogenicStageColor(0) },
            { key: MISSING_DATA_KEY, label: i18n.t("Donnée manquante"), color: MISSING_DATA_COLOR },
            { key: EMPTY_LOCATION_KEY, label: i18n.t("Emplacement vide"), color: ClassificationUtil.getOntogenicStageColor() }
        ];

        return { propertyName: 'ontogenicStageId', getKey, getColor, getBuckets };
    };

    static getHeightClassification = () => {
        const activeChild = i18n.t("Hauteurs");

        const getKey = (properties) => {
            if (ClassificationUtil.isDisplayedAsEmptyLocation(activeChild, properties)) return EMPTY_LOCATION_KEY;
            const height = ClassificationUtil.getTreeHeight(properties);
            return height ? HEIGHT_BUCKETS.find(bucket => bucket.isIncluded(height)).key : MISSING_DATA_KEY;
        };

        const getColor = (key) => key === EMPTY_LOCATION_KEY ? EMPTY_LOCATION_COLOR
            : HEIGHT_BUCKETS.find(bucket => bucket.key === key)?.color || MISSING_DATA_COLOR;

        const getBuckets = () => [
            ...HEIGHT_BUCKETS.map(({ key, label, color }) => ({ key, label, color })),
            { key: MISSING_DATA_KEY, label: i18n.t("Donnée manquante"), color: MISSING_DATA_COLOR },
            { key: EMPTY_LOCATION_KEY, label: i18n.t("Emplacement vide"), color: EMPTY_LOCATION_COLOR, hideWhenEmpty: true }
        ];

        return { propertyName: 'height', getKey, getColor, getBuckets };
    };

    static getAgeClassification = () => {
        const activeChild = i18n.t("Âges");
        const labels = [
            i18n.t("Aucun âge"), i18n.t("{{range}} ans", { range: '0-15' }), i18n.t("{{range}} ans", { range: '16-50' }),
            i18n.t("{{range}} ans", { range: '51-100' }), i18n.t("{{range}} ans", { range: '101-200' }),
            i18n.t("{{range}} ans", { range: '201-300' }), i18n.t("{{range}} ans", { range: '+ 300' })
        ];

        const getKey = (properties) => ClassificationUtil.isDisplayedAsEmptyLocation(activeChild, properties)
            ? EMPTY_LOCATION_KEY
            : String(TreesUtil.getAgeIndex(properties.age));

        const getColor = (key) => key === EMPTY_LOCATION_KEY ? EMPTY_LOCATION_COLOR : AGE_COLORS[Number(key)];

        const getBuckets = () => [
            ...labels.map((label, index) => ({ key: String(index), label, color: AGE_COLORS[index] })),
            { key: EMPTY_LOCATION_KEY, label: i18n.t("Emplacement vide"), color: EMPTY_LOCATION_COLOR, hideWhenEmpty: true }
        ];

        return { propertyName: 'age', getKey, getColor, getBuckets };
    };

    static getCutDownClassification = () => {
        const labels = [
            i18n.t("Aucun abattage"), i18n.t("Abattage > 3 ans"), i18n.t("Abattage 1 - 3 ans"),
            i18n.t("Abattage < 1 an"), i18n.t("Abattage d'urgence"), i18n.t("Abattu")
        ];

        return {
            propertyName: 'toCutDown',
            getKey: (properties) => String(properties.toCutDown || 0),
            getColor: (key) => ClassificationUtil.getCutDownColor(Number(key)),
            getBuckets: () => labels.map((label, index) => ({ key: String(index), label, color: ClassificationUtil.getCutDownColor(index) })).reverse()
        };
    };

    static getMortalityClassification = () => {
        const labels = {
            alive: i18n.t("Vif"), dead: i18n.t("Mort"), stump: i18n.t("Mort + Souche"),
            chronoxyle: i18n.t("Mort + Chronoxyle"), deadEmpty: i18n.t("Mort + Vide"), empty: i18n.t("Vide")
        };

        return {
            propertyName: 'mortality',
            getKey: ClassificationUtil.getMortalityKey,
            getColor: (key) => MORTALITY_COLORS[key],
            getBuckets: () => Object.keys(labels).map(key => ({ key, label: labels[key], color: MORTALITY_COLORS[key] }))
        };
    };

    static getActionsUrgencyClassification = () => {
        const activeChild = i18n.t("Actions");
        const labels = [
            i18n.t("Pas d'action"), i18n.t("Annulée"), i18n.t("Validée"),
            i18n.t("1 mois & +"), i18n.t("< 1 mois"), i18n.t("En retard")
        ];

        const getKey = (properties) => ClassificationUtil.isDisplayedAsEmptyLocation(activeChild, properties)
            ? EMPTY_LOCATION_KEY
            : String(properties.actionsUrgency || 0);

        const getColor = (key) => key === EMPTY_LOCATION_KEY ? EMPTY_LOCATION_COLOR : ClassificationUtil.getActionsUrgencyColor(Number(key));

        const getBuckets = () => [
            ...labels.map((label, index) => ({ key: String(index), label, color: ClassificationUtil.getActionsUrgencyColor(index) })).reverse(),
            { key: EMPTY_LOCATION_KEY, label: i18n.t("Emplacement vide"), color: EMPTY_LOCATION_COLOR, hideWhenEmpty: true }
        ];

        return { propertyName: 'actionsUrgency', getKey, getColor, getBuckets };
    };

    static getDominantCompositionClassification = (fieldList = {}) => {
        const dominantCompositions = fieldList.dominantCompositions || [];
        const labelById = new Map(dominantCompositions.map(field => [field.id, field.label]));

        const getKey = (properties) => labelById.has(properties.dominantCompositionId)
            ? String(properties.dominantCompositionId)
            : MISSING_DATA_KEY;

        const getColor = (key) => key === MISSING_DATA_KEY
            ? MISSING_DATA_COLOR
            : ClassificationUtil.getDominantCompositionColor(labelById.get(Number(key)));

        const getFill = (key) => key === MISSING_DATA_KEY
            ? { fillColor: MISSING_DATA_COLOR }
            : ClassificationUtil.getDominantCompositionColor(labelById.get(Number(key)), false);

        const getBuckets = () => [
            ...dominantCompositions.map(field => ({ key: String(field.id), label: field.label, color: ClassificationUtil.getDominantCompositionColor(field.label) })),
            { key: MISSING_DATA_KEY, label: i18n.t("Donnée manquante"), color: MISSING_DATA_COLOR }
        ];

        return { propertyName: 'dominantCompositionId', getKey, getColor, getFill, getBuckets };
    };

    static getManagementClassClassification = (fieldList = {}) => {
        const managementClasses = fieldList.managementClasses || [];
        const valueById = new Map(managementClasses.map(field => [field.id, field.value]));

        return {
            propertyName: 'managementClassId',
            getKey: (properties) => valueById.has(properties.managementClassId) ? String(properties.managementClassId) : MISSING_DATA_KEY,
            getColor: (key) => key === MISSING_DATA_KEY ? MISSING_DATA_COLOR : ClassificationUtil.getManagementClassColor(valueById.get(Number(key))),
            getBuckets: () => [
                ...managementClasses.map(field => ({ key: String(field.id), label: `${i18n.t("Classe")} ${field.value}`, color: ClassificationUtil.getManagementClassColor(field.value) })),
                { key: MISSING_DATA_KEY, label: i18n.t("Donnée manquante"), color: MISSING_DATA_COLOR }
            ]
        };
    };

    static getStationClassification = (fieldList = {}) => ({
        propertyName: 'level',
        getKey: (properties) => String(properties.level || 1),
        getColor: (key) => ClassificationUtil.getStationColor(Number(key), fieldList),
        getBuckets: () => [1, 2, 3].map(level => ({
            key: String(level),
            label: (fieldList.stationLevels || []).find(({ level: stationLevel }) => stationLevel === level)?.label?.trim() || `${i18n.t("Niveau")} ${level}`,
            color: ClassificationUtil.getStationColor(level, fieldList)
        }))
    });
}
